import { useCallback, useRef } from "react";

const isFocusOnBody = () =>
    document.activeElement === null || document.activeElement === document.body;

const isFocusIn = (element: HTMLElement | null) =>
    element?.contains(document.activeElement) ?? false;

/**
 * Keeps keyboard focus when the nav hides or shows. `noteHiding` runs before
 * every state change that hides the nav and records whether focus was inside
 * it; once hidden, `returnFocus` moves focus to `SideNavShowButton`. Likewise
 * `noteShowing` records whether focus was on the show button or in the nav,
 * and once shown `focusNav` moves it to the nav's first control. Neither moves
 * focus that something else (e.g. the next page) has taken meanwhile.
 */
export const useSideNavFocusReturn = () => {
    const navRef = useRef<HTMLElement | null>(null);
    const showButtonRef = useRef<HTMLButtonElement | null>(null);
    const shouldFocusShowButtonRef = useRef(false);
    const shouldFocusNavRef = useRef(false);

    const noteHiding = useCallback(() => {
        shouldFocusNavRef.current = false;
        shouldFocusShowButtonRef.current = isFocusIn(navRef.current);
    }, []);

    const noteShowing = useCallback(() => {
        shouldFocusShowButtonRef.current = false;
        shouldFocusNavRef.current =
            document.activeElement === showButtonRef.current ||
            isFocusIn(navRef.current);
    }, []);

    const returnFocus = useCallback(() => {
        if (!shouldFocusShowButtonRef.current) {
            return;
        }
        shouldFocusShowButtonRef.current = false;
        if (isFocusOnBody() || isFocusIn(navRef.current)) {
            showButtonRef.current?.focus();
        }
    }, []);

    const focusNav = useCallback(() => {
        if (!shouldFocusNavRef.current) {
            return;
        }
        shouldFocusNavRef.current = false;
        const firstControl = navRef.current?.querySelector(
            "a[href], button:not([disabled])",
        );
        if (isFocusOnBody() && firstControl instanceof HTMLElement) {
            firstControl.focus();
        }
    }, []);

    return {
        navRef,
        showButtonRef,
        noteHiding,
        noteShowing,
        returnFocus,
        focusNav,
    };
};
